// The Agents subtab, and the /agents page: the new-agent box, "Now & next" (NowNext: what the agents
// are doing and what runs next, hidden when there is nothing), your agents, the agents shared with you,
// and one agent's chat when opened. A row of "Now & next" opens its agent as a row of the lists does.
//
// Each row says what the agent is (its type and mode), when it runs (statusLabel: its status when it
// takes no runs, else its schedule), how many of its updates the person hasn't seen (`unseen`, which
// counts its waiting cards too), and "Needs you" while cards wait for a decision (`pending_cards`). A
// paused agent's cards wait for its owner, so its row says "Needs you", a middle dot and "paused", in
// grey, as something waiting rather than a call to act. A count the server couldn't read (null) shows
// nothing.
//
// Each of your agents' rows has a pencil and a trash can (never in a view-as session, which changes
// nothing). The pencil turns the title into a box: Enter or Save renames it (PATCH, the title alone),
// Escape or Cancel leaves it as it was, and the list and the open chat are read again once it is
// saved. The trash can asks once more, in the row, with Cancel where the trash can was (so a double
// click never deletes); Delete deletes the agent for everyone on it (its chat and history go too),
// closes its chat if it is open, and reads the list again. A refusal is the server's sentence, under
// the row. An answer that never came reads the list again, which shows whether the change was made.
//
// The list comes from useAgents (agentsState). `enabled` is the server's switch; it is false too while
// the first list loads and after a list that failed to load, so "switched off" is said only when the
// list loaded and said so.
//
// Layouts:
// - stacked (the docked panel and the popped-out window): the list, or one agent's chat in its place
//   (the list stays mounted, hidden, so the new-agent box keeps its words and its plan card);
// - split (the /agents page): on a wide screen (lg and up) the list stays on the left with the open
//   agent marked, and the chat is on the right; on a narrow one it is stacked as above.
//
// Props: agentsState (useAgents' answer); userId; patient and schema (the open chart, for the new
// agent's "This patient"; the schema is also the one the members search looks in); focus
// ({agentId, runId} from a link: that agent opens, at that run); split; onOpenChange (optional: told
// the agent's id when the person opens or creates one, and null when they go back to the list, so the
// /agents page can keep it in its address; never told of an agent a link opened, which it has).
import React, { useEffect, useId, useRef, useState } from 'react';
import AgentChat from './AgentChat';
import NewAgentBox from './NewAgentBox';
import NowNext from './NowNext';
import { agentErrorText } from './agentErrors';
import { deleteAgent, updateAgent } from './agentsService';
// HALTED: the statuses in which an agent takes no new run (types.NO_NEW_RUNS): its cards can't be decided.
import { HALTED } from './nowNextRows';
import { statusLabel } from './triggerText';
import { NO_CONNECTION, lostAnswer } from './usePlanRequest';
import { isImpersonating } from '../../../utils/impersonation';

// The characters the words use that aren't ASCII, so this file stays ASCII.
const ELLIPSIS = String.fromCharCode(0x2026);
const DOT = String.fromCharCode(0xb7);
const QUOTE = String.fromCharCode(0x201c);
const UNQUOTE = String.fromCharCode(0x201d);

// What a title may hold (views.MAX_TITLE); the server refuses more with its own sentence.
const MAX_TITLE = 200;
// Refusals after which the list may be behind (the agent is gone, isn't the person's, or changed under
// the request): it is read again. A refusal of the words themselves (400) changes nothing there.
const STALE = new Set([403, 404, 409]);

const HEADING = 'text-[11px] font-bold uppercase tracking-wide text-slate-400 dark:text-slate-500 mb-1.5';
const ROW = 'flex items-center rounded-lg border';
const ROW_BUTTON = 'flex-1 min-w-0 self-stretch text-left px-3 py-2';
const ICON = 'p-1 rounded text-slate-400 dark:text-slate-500 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800';
const ICON_DANGER = 'p-1 rounded text-slate-400 dark:text-slate-500 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10';
const TITLE_BOX = 'flex-1 min-w-0 rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 py-1 text-xs text-slate-800 dark:text-slate-100';
const SAVE = 'shrink-0 rounded-md px-2 py-1 text-[11px] font-semibold bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white';
const CANCEL = 'shrink-0 rounded-md px-2 py-1 text-[11px] font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-50';
const DELETE = 'shrink-0 rounded-md px-2 py-1 text-[11px] font-semibold bg-red-600 dark:bg-red-500 hover:bg-red-500 dark:hover:bg-red-400 text-white';
const QUESTION = 'flex-1 min-w-0 text-xs text-slate-700 dark:text-slate-200';
const DELETING = 'flex-1 px-3 py-2 text-xs text-slate-500 dark:text-slate-400';
const ROW_ERROR = 'mt-1 px-1 text-[11px] text-red-600 dark:text-red-400';
const ROW_IDLE = 'border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/40 hover:bg-slate-50 dark:hover:bg-slate-800/40';
const ROW_OPEN = 'border-blue-300 dark:border-blue-500/40 ring-1 ring-blue-300 dark:ring-blue-500/40 bg-blue-50/60 dark:bg-blue-500/[0.08]';
const PILL = 'px-1.5 py-0.5 rounded text-[10px] font-bold';
const NEEDS = 'bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300';
const WAITING = 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300';
const BADGE = 'inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold bg-rose-600 dark:bg-rose-500 text-white';
const NOTE = 'mx-3 my-1 text-[11px] text-slate-500 dark:text-slate-400';
const ERROR = 'mx-3 my-1 flex items-center justify-between gap-2 text-[11px] text-red-600 dark:text-red-400';
const RETRY = 'shrink-0 rounded-md px-2 py-0.5 text-[11px] font-semibold text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-500/10';

const typeLabel = (types, key) => types.find((item) => item.key === key)?.label || key;

// Every agent has its own thread in Messages (the same conversation as its chat), and the row says
// so with a small speech balloon. It is the same on every row, so screen readers skip it.
function ThreadIcon() {
  return (
    <svg className="w-3 h-3 shrink-0 text-slate-400 dark:text-slate-500" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" aria-hidden="true">
      <title>Has a thread in Messages</title>
      <path strokeLinecap="round" strokeLinejoin="round"
            d="M8 10h8M8 14h5M21 12c0 4.418-4.03 8-9 8a9.86 9.86 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
    </svg>
  );
}

function PencilIcon() {
  return (
    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round"
            d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round"
            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
    </svg>
  );
}

// One agent's row. `editable` (your own agents, outside a view-as session) adds the pencil and the
// trash can; `onRenamed()` and `onDeleted(agentId)` tell the pane, and `onReread()` reads the list
// again. Each resolves once the list is read, so the row stays as it is until then, rather than show
// the old title, or a deleted agent, for a moment.
function AgentRow({ agent, types, open, onOpen, editable = false, onRenamed, onDeleted, onReread }) {
  const [mode, setMode] = useState('view'); // 'view', 'rename', 'ask' (delete it?) or 'deleting'
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const alive = useRef(true);
  const renameButton = useRef(null);
  const deleteButton = useRef(null);
  const cancelButton = useRef(null);
  const titleBox = useRef(null);
  const refocus = useRef(null); // back on the row: the button that takes focus, 'rename' or 'delete'

  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  // The box takes focus with its words selected, the question gives it to Cancel, and back on the row
  // it returns to the button the person pressed.
  useEffect(() => {
    if (mode === 'rename') {
      titleBox.current?.focus();
      titleBox.current?.select();
    } else if (mode === 'ask') {
      cancelButton.current?.focus();
    } else if (mode === 'view' && refocus.current) {
      (refocus.current === 'rename' ? renameButton : deleteButton).current?.focus();
      refocus.current = null;
    }
  }, [mode]);

  const backToRow = (button) => {
    refocus.current = button;
    setMode('view');
  };
  const onEscape = (event) => {
    if (event.key !== 'Escape' || saving) return;
    event.preventDefault();
    setError(null);
    backToRow(mode === 'rename' ? 'rename' : 'delete');
  };
  // What a failed request says, after the list is read again when it may be behind.
  const failed = async (err) => {
    const failure = err || new Error('');
    const lost = lostAnswer(failure);
    if (lost || STALE.has(failure.status)) await onReread();
    return lost ? NO_CONNECTION : agentErrorText(failure);
  };

  const startRename = () => {
    setDraft(agent.title || '');
    setError(null);
    setMode('rename');
  };
  const save = async (event) => {
    event.preventDefault();
    const title = draft.trim();
    if (saving || !title) return;
    if (title === agent.title) {
      backToRow('rename');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await updateAgent(agent.id, { title });
    } catch (err) {
      const words = await failed(err);
      if (!alive.current) return;
      setSaving(false);
      setError(words);
      return;
    }
    await onRenamed();
    if (!alive.current) return;
    setSaving(false);
    backToRow('rename');
  };

  const ask = () => {
    setError(null);
    setMode('ask');
  };
  const remove = async () => {
    setMode('deleting');
    try {
      await deleteAgent(agent.id);
    } catch (err) {
      const words = await failed(err);
      if (!alive.current) return;        // the list read again no longer has it: it was deleted
      setError(words);
      backToRow('delete');
      return;
    }
    onDeleted(agent.id);
    await onReread();
    if (alive.current) setMode('view');  // still listed: that read failed, or an older one won
  };

  const unseen = Number.isFinite(agent.unseen) && agent.unseen > 0 ? agent.unseen : 0;
  const waiting = Number.isInteger(agent.pending_cards) && agent.pending_cards > 0;
  const halted = HALTED.has(agent.status);
  const named = `${QUOTE}${agent.title}${UNQUOTE}`;
  let body;
  if (mode === 'rename') {
    body = (
      <form aria-label={`Rename ${agent.title}`} onSubmit={save} onKeyDown={onEscape}
            className="flex-1 flex items-center gap-1.5 px-2 py-1.5">
        <input ref={titleBox} type="text" aria-label="Agent title" value={draft} maxLength={MAX_TITLE} readOnly={saving}
               onChange={(event) => setDraft(event.target.value)} className={TITLE_BOX} />
        <button type="submit" disabled={saving || !draft.trim()} className={SAVE}>{saving ? `Saving${ELLIPSIS}` : 'Save'}</button>
        <button type="button" disabled={saving} onClick={() => { setError(null); backToRow('rename'); }} className={CANCEL}>
          Cancel
        </button>
      </form>
    );
  } else if (mode === 'ask') {
    body = (
      <div role="group" aria-label={`Delete ${agent.title}?`} onKeyDown={onEscape} className="flex-1 flex items-center gap-2 px-3 py-2">
        <span className={QUESTION}>{`Delete ${named}? Its chat and history go with it.`}</span>
        <button type="button" onClick={remove} className={DELETE}>Delete</button>
        <button ref={cancelButton} type="button" onClick={() => backToRow('delete')} className={CANCEL}>Cancel</button>
      </div>
    );
  } else if (mode === 'deleting') {
    body = <div role="status" className={DELETING}>{`Deleting ${named}${ELLIPSIS}`}</div>;
  } else {
    body = (
      <>
        <button type="button" onClick={() => onOpen(agent.id)} aria-current={open ? 'true' : undefined} className={ROW_BUTTON}>
          <div className="flex items-center justify-between gap-2">
            <span className="flex items-center gap-1 min-w-0">
              <span className="text-xs font-semibold text-slate-800 dark:text-slate-100 truncate">{agent.title}</span>
              <ThreadIcon />
            </span>
            {(waiting || unseen > 0) && (
              <span className="flex items-center gap-1.5 shrink-0">
                {waiting && (
                  <span className={`${PILL} ${halted ? WAITING : NEEDS}`}>{halted ? `Needs you ${DOT} ${agent.status}` : 'Needs you'}</span>
                )}
                {unseen > 0 && <span role="img" aria-label={`${unseen} unseen`} className={BADGE}>{unseen}</span>}
              </span>
            )}
          </div>
          <div className="text-[11px] text-slate-500 dark:text-slate-400 truncate">
            {typeLabel(types, agent.type)}{` ${DOT} `}{agent.mode === 'auto' ? 'Auto' : 'Ask'}{` ${DOT} `}{statusLabel(agent)}
          </div>
        </button>
        {editable && (
          <span className="shrink-0 flex items-center gap-0.5 pr-2">
            <button ref={renameButton} type="button" aria-label={`Rename ${agent.title}`} title="Rename" onClick={startRename} className={ICON}>
              <PencilIcon />
            </button>
            <button ref={deleteButton} type="button" aria-label={`Delete ${agent.title}`} title="Delete" onClick={ask} className={ICON_DANGER}>
              <TrashIcon />
            </button>
          </span>
        )}
      </>
    );
  }
  return (
    <li>
      <div className={`${ROW} ${open ? ROW_OPEN : ROW_IDLE}`}>{body}</div>
      {error && <div role="alert" className={ROW_ERROR}>{error}</div>}
    </li>
  );
}

// A list is named by its heading. `editable`: its rows have the pencil and the trash can.
function AgentList({ title, agents, types, openId, onOpen, editable = false, onRenamed, onDeleted, onReread }) {
  const titleId = useId();
  if (!agents.length) return null;
  return (
    <div className="px-3 py-2">
      <div id={titleId} className={HEADING}>{title}</div>
      <ul aria-labelledby={titleId} className="list-none m-0 p-0 space-y-1.5">
        {agents.map((agent) => (
          <AgentRow key={agent.id} agent={agent} types={types} open={openId === agent.id} onOpen={onOpen}
                    editable={editable} onRenamed={onRenamed} onDeleted={onDeleted} onReread={onReread} />
        ))}
      </ul>
    </div>
  );
}

export default function AgentsPane({
  agentsState, userId = null, patient = null, schema = null, focus = null, split = false, onOpenChange = null,
}) {
  const {
    agents = [], types = [], loading = false, error = null, enabled = false, worker = true, runtime = 'ok',
    refresh, version = 0,
  } = agentsState || {};
  const linkedAgent = focus?.agentId || null;
  const linkedRun = focus?.runId || null;
  const [openId, setOpenId] = useState(linkedAgent);
  // A row's request answers after renders, so it is told the agent open then, not when it was sent.
  const openNow = useRef(openId);
  openNow.current = openId;
  // The run a link points at, for the chat it opened; gone once that chat is left.
  const [runFocus, setRunFocus] = useState(linkedRun);
  // Renames from the list: the agents table isn't on realtime, so each one is added to the version the
  // open chat reads its agent again on, for its header to show the new title at once.
  const [renames, setRenames] = useState(0);
  useEffect(() => {
    if (!linkedAgent) return;
    setOpenId(linkedAgent);
    setRunFocus(linkedRun);
  }, [linkedAgent, linkedRun]);

  const list = Array.isArray(agents) ? agents : [];
  const switchedOff = !enabled && !loading && !error;
  const open = (agentId) => {
    setRunFocus(null);
    setOpenId(agentId);
    onOpenChange?.(agentId);
  };
  const back = () => {
    setRunFocus(null);
    setOpenId(null);
    refresh?.();
    onOpenChange?.(null);
  };
  const created = (agent) => {
    refresh?.();
    if (agent?.id) open(agent.id);
  };
  const reread = async () => { await refresh?.(); };
  const renamed = async () => {
    setRenames((count) => count + 1);
    await reread();
  };
  const deleted = (agentId) => {
    if (openNow.current !== agentId) return;
    setRunFocus(null);
    setOpenId(null);
    onOpenChange?.(null);
  };
  // In a view-as session every agents endpoint refuses a change, so the rows offer none. Entering and
  // leaving one both load the page afresh, so this can't change under it.
  const editable = !isImpersonating();

  const listBody = (
    <>
      <NewAgentBox patient={patient} schema={schema} types={types} disabled={switchedOff} onCreated={created} />
      {switchedOff && <div className={NOTE}>Agents are switched off on this server.</div>}
      {enabled && !loading && !error && worker === false && (
        <div className={NOTE}>This server isn't running the agents worker, so its runs stay queued.</div>
      )}
      {enabled && !loading && !error && runtime === 'missing' && (
        <div className={NOTE}>
          This server has no agent service (Hermes) set up, so its runs can't start. On a computer, start the
          backend with run_services.py while Docker Desktop is open.
        </div>
      )}
      {enabled && !loading && !error && runtime === 'unreachable' && (
        <div className={NOTE}>
          The agent service isn&apos;t answering right now, so runs wait in the queue (for up to 30 minutes)
          until it is back.
        </div>
      )}
      {error && (
        <div role="alert" className={ERROR}>
          <span>{agentErrorText(error)}</span>
          <button type="button" onClick={() => refresh?.()} className={RETRY}>Try again</button>
        </div>
      )}
      {loading && list.length === 0 && <div className={NOTE}>{`Loading agents${ELLIPSIS}`}</div>}
      <NowNext agents={list} onOpen={open} />
      <AgentList title="Your agents" agents={list.filter((a) => a.is_owner)} types={types} openId={split ? openId : null} onOpen={open}
                 editable={editable} onRenamed={renamed} onDeleted={deleted} onReread={reread} />
      <AgentList title="Shared with you" agents={list.filter((a) => !a.is_owner)} types={types} openId={split ? openId : null} onOpen={open} />
      {!loading && !error && enabled && list.length === 0 && (
        <div className="mx-3 my-3 text-xs text-slate-500 dark:text-slate-400">No agents yet. Tell one what to do above.</div>
      )}
    </>
  );
  const chat = openId && (
    <AgentChat key={openId} agentId={openId} userId={userId} types={types} version={version + renames} schema={schema}
               focusRunId={openId === linkedAgent ? runFocus : null} draft={openId === linkedAgent ? focus?.ask || null : null}
               split={split} onBack={back} onChanged={refresh}
               onOpenAgent={open} openableAgents={list.map((agent) => agent.id)} />
  );

  if (!split) {
    return (
      <>
        <div role="region" aria-label="Agents list" hidden={!!chat} className="flex-1 overflow-y-auto scrollbar-thin"
             style={{ minHeight: 0 }}>
          {listBody}
        </div>
        {chat}
      </>
    );
  }
  return (
    <div className="flex-1 flex" style={{ minHeight: 0 }}>
      <div role="region" aria-label="Agents list"
           className={`${openId ? 'hidden lg:block' : 'block'} w-full lg:w-[360px] lg:shrink-0 lg:border-r border-slate-200 dark:border-slate-800/60 overflow-y-auto scrollbar-thin`}
           style={{ minHeight: 0 }}>
        {listBody}
      </div>
      <div className={`${openId ? 'flex' : 'hidden lg:flex'} flex-1 flex-col min-w-0`} style={{ minHeight: 0 }}>
        {chat || (
          <div className="flex-1 flex items-center justify-center px-6 text-center text-xs text-slate-500 dark:text-slate-400">
            Pick an agent to open its chat.
          </div>
        )}
      </div>
    </div>
  );
}
